import { useContext, useState, useLayoutEffect, useEffect } from "react";
import { useLocation, useParams } from 'react-router-dom';
import Context from '../../../context';
import './index.scss';
import IconButton from "@mui/material/IconButton";
import { Link } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HamburgerMenu from "../HamburgerMenu";
import PhotobookIcon from "../Icons/Photobook";
import { BACK_NAVIGATION_TYPE, DOC_ID_INDEX_FROM_END } from "../../../helpers/constants";
import { getEwaBackPath } from "../../../libraries/ewa/helpers";
import { EwaContext } from "../../../context/EwaContext";

const MobileHeader = props => {
  const {
    customHistory,
    removeItemsFromCustomHistory,
    mobileViewGeneric,
    screenNameOverride,
    itin,
    composerData,
    t,
    previewMode, backPathOverride
  } = useContext(Context);
  const { screenNameReady } = useContext(EwaContext);

  const [backButtonPath, setBackButtonPath] = useState(`/${itin?.localData?.urlKey || ''}`);
  const [screenName, setScreenName] = useState('');

  const { slideNum } = useParams();
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    let backPath;
    const ewaBackPath = getEwaBackPath();
    if (ewaBackPath && pathname === ewaBackPath?.entryPath && props?.backButtonPath !== BACK_NAVIGATION_TYPE) {
      backPath = ewaBackPath?.returnTo;
    } else if (backPathOverride) {
      backPath = backPathOverride;
    } else if (props?.backButtonPath !== undefined && props?.backButtonPath !== null) {
      backPath = props.backButtonPath;
    } else {
      let newPath = pathname.split('/').filter(x => x !== '');
      newPath.pop();
      newPath = '/' + newPath.join('/');

      let i = 0;
      let target;
      while(true) {
        target = customHistory[customHistory.length + i - 1];
        if (!target) {
          backPath = newPath;
          break;
        }
        if (target.match(new RegExp('^' + newPath + '[/]?$', 'i'))) {
          backPath = i;
          break;
        }
        i--;
      }
    }

    if (typeof backPath === 'number') backPath = customHistory[customHistory?.length + (backPath - 1)] ?? '/';

    // after viewing PDF files on the Stay (where Directory logic is used), the back button always navigates to a page like /directory/6866172/view
    // when a user clicks on a document at the top level, it always adds '/documents/SMTH'.
    // and this is used to fix incorrect navigation
    if (backPath && typeof backPath === 'string') {
      const cleanupSuffixes = ['/view', '/documents'];
      const matchedSuffix = cleanupSuffixes.find(s => backPath.endsWith(s));
      if (matchedSuffix) backPath = backPath.replace(new RegExp(`${matchedSuffix}.*$`), '');
    }

    setBackButtonPath(backPath);
  }, [backPathOverride, props.backButtonPath, customHistory, pathname]);

  useEffect(() => {
    let newScreenName;
    if (screenNameOverride || screenNameOverride === '') {
      newScreenName = screenNameOverride;
    } else if (props?.docScreenName) {
      newScreenName = props?.docScreenName;
    } else if (itin?.type === 'inspiration' && pathname.startsWith(`/${itin.localData.urlKey}/inspirations`)) {
      newScreenName = itin.overlay.label;
    } else {
      let sn = '';
      if (!previewMode) {
        const parts = pathname.split('/');
        let positionIdx = 1;
        if (itin?.localData?.urlKey && pathname.startsWith(`/${itin.localData.urlKey}`)) {
          positionIdx = 2;
        }
        sn = parts[positionIdx]?.replace(/-/g, ' ')?.toLowerCase();
        const isDocumentGallery = sn === 'documents' && parts[parts.length - 2] === 'gallery';

        if (isDocumentGallery) {
          const menuItem = itin.documents
            .filter(docItem => itin.menu.some(menuItem => menuItem.segueId === docItem.id))
            .find(doc => doc.pathName === parts[parts.length - DOC_ID_INDEX_FROM_END]);

            const documentId = menuItem
              ? menuItem.entries[parseInt(slideNum) - 1]
              : itin.documents?.find(doc => doc.id === parseInt(parts[parts.length - DOC_ID_INDEX_FROM_END]))?.entries[parseInt(slideNum) - 1];

          const documentGalleryItem = itin.documents?.find(doc => doc.id === documentId);
          if (documentGalleryItem) newScreenName = documentGalleryItem.name;
        }
      }
      if (!newScreenName && itin?.menu) newScreenName = itin.menu.find(x => x.segue.toLowerCase() === sn)?.label; // TODO* myDoc check how this works with the new docs
      if (!newScreenName) {
        const translationsMap = {
          faq: 'faq',
          terms: 'terms',
          about: 'about_us',
          settings: 'settings',
          'view all': 'menu_view_all'
        };

        newScreenName = composerData?.menu.documents?.[sn]?.title;
        if (!newScreenName && translationsMap[sn]) newScreenName = t(translationsMap[sn]);
        if (!newScreenName) newScreenName = '';
      }
    }

    setScreenName(newScreenName);

    return () => setScreenName('');
  }, [itin?.overlay?.label, itin?.documents, slideNum, props?.docScreenName, itin?.menu, t, pathname, screenNameOverride, itin?.type, composerData, itin?.localData?.urlKey, previewMode]);

  const renderLeftButton = () => {
    const isPreviewMapsView = /^\/preview-maps\/[^/]+\/\d+\/view\/\d+\/?$/.test(pathname);
    if (previewMode && !isPreviewMapsView && (!parseInt(slideNum) || (!pathname.includes('/view/') && pathname.match(new RegExp(`^/preview.*/.*/\\d+[/]?$`))))) return null; //to display the back button on previewmode for all pages except page without slideNum and which have previewLinkId and doesn't end with digits and doesn't include /view/ and not a view in preview-maps mode

    if (props.itineraryType === 'inspiration' && (itin && pathname.startsWith(`/${itin.localData.urlKey}/inspirations`))) {
      return <HamburgerMenu hamburgerBtnClassName={props.buttonColor} />;
    }

    return (
      <IconButton
        className={`back-button button ${props.buttonColor}`}
        aria-label="Go back"
        component={backButtonPath !== BACK_NAVIGATION_TYPE ? Link : "button"}
        to={backButtonPath !== BACK_NAVIGATION_TYPE ? backButtonPath : undefined}
        onClick={(e) => {
          if (backButtonPath === BACK_NAVIGATION_TYPE) {
            e.preventDefault();
            removeItemsFromCustomHistory(-1);
            window.history.back();
            props.backButtonClickFn?.();
          } else {
            removeItemsFromCustomHistory(backButtonPath);
            props.backButtonClickFn?.();
          }
        }}
      >
        <ArrowBackIcon className="back-button-icon" />
      </IconButton>
    );
  };

  const renderRightButton = () => {
    if (!props.mobileHeaderButtonRight) return null;
    return (
      <IconButton
        className={`right-button button ${props.mobileHeaderButtonRight.className ? props.mobileHeaderButtonRight.className : ''} ${props.buttonColor}`}
        component={props.mobileHeaderButtonRight.component || null}
        to={props.mobileHeaderButtonRight.to || null}
        onClick={() => {props.mobileHeaderButtonRight.onClick && props.mobileHeaderButtonRight.onClick()}}
      >
        {props.mobileHeaderButtonRight.icon}
      </IconButton>
    );
  };

  return (
    <div className={`mobile-header ${props?.headerBackGroundColor ?? 'header'}  ${mobileViewGeneric ? 'mobile-view-generic' : ''} ${props.buttonColor}`}>
      { !props.hideLeftButton && renderLeftButton() }
      <div
        className={`screenname ${props.textColorOverride ? props.textColorOverride : ''}`}
      >
        {screenNameReady && screenName}
      </div>
      { renderRightButton() }
      {
        itin?.photobookLink && pathname.includes('posts') &&
        <div className="photobook-icon-container">
          <PhotobookIcon
            icon={'/icons/photobook_link_icon_3x.png'}
            photobookLink={itin?.photobookLink}
          />
        </div>
      }
    </div>
  );
};

export default MobileHeader;
